/**
 * KNK-1.22 / FR-8 — invite reps from Settings → Knock Mode, and see or revoke what was sent.
 *
 * ⚠️ Nothing here is the gate. `create_knock_invite` decides who may invite (field.settings.manage,
 * manage_users, user.manage_all, or a team lead onto their own team), that an invite only makes a
 * Sales Rep, and that a number can be texted. Its refusal sentence is shown as it is.
 * Team leads invite from the phone; this card is on the admin's Knock Mode page.
 *
 * Rules and copy: `lib/knockInvite.ts`.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Copy, Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabaseClient';
import { JOIN_COPY as COPY, inviteFormError, type CreatedInvite, type InviteRow } from '@/lib/knockInvite';
import { createKnockInvite, listKnockInvites, revokeKnockInvite } from '@/services/knockInviteService';

const NO_TEAM = '__none__';
const EMPTY = { firstName: '', lastName: '', phone: '', email: '' };

export function KnockInvitesCard() {
  const { toast } = useToast();
  const [rows, setRows] = useState<InviteRow[] | null>(null);
  const [teams, setTeams] = useState<{ id: number; name: string }[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [teamId, setTeamId] = useState<string>(NO_TEAM);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedInvite | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await listKnockInvites());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
    // Teams of the active account, by RLS. The function re-checks the team belongs to it.
    void supabase
      .from('teams')
      .select('id, name')
      .order('name')
      .then(({ data }) => setTeams((data ?? []) as { id: number; name: string }[]));
  }, [load]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    const problem = inviteFormError(form);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setBusy('create');
    try {
      setCreated(await createKnockInvite({ ...form, teamId: teamId === NO_TEAM ? null : Number(teamId) }));
      setForm(EMPTY);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (row: InviteRow) => {
    if (!window.confirm(COPY.revokeConfirm(row.label))) return;
    setBusy(row.id);
    try {
      await revokeKnockInvite(row.id);
      await load();
    } catch (err) {
      toast({ title: 'Not revoked', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: COPY.copied });
    } catch {
      toast({ title: 'Copy failed', description: link, variant: 'destructive' });
    }
  };

  const set = (key: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Card data-testid="knock-invites">
      <CardHeader>
        <CardTitle>{COPY.inviteTitle}</CardTitle>
        <CardDescription>{COPY.inviteHint}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={create} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="invite-first" label={COPY.firstNameLabel}>
              <Input id="invite-first" value={form.firstName} onChange={set('firstName')} />
            </Field>
            <Field id="invite-last" label={COPY.lastNameLabel}>
              <Input id="invite-last" value={form.lastName} onChange={set('lastName')} />
            </Field>
            <Field id="invite-phone" label={COPY.phoneLabel}>
              <Input id="invite-phone" type="tel" value={form.phone} onChange={set('phone')} />
            </Field>
            <Field id="invite-email" label={COPY.emailLabel}>
              <Input id="invite-email" type="email" value={form.email} onChange={set('email')} />
            </Field>
            <Field id="invite-team" label="Team">
              <Select value={teamId} onValueChange={setTeamId}>
                <SelectTrigger id="invite-team">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TEAM}>No team</SelectItem>
                  {teams.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Button type="submit" disabled={busy !== null}>
            {busy === 'create' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {COPY.create}
          </Button>
        </form>

        {created && (
          <Alert data-testid="knock-invite-created">
            <AlertDescription className="space-y-2">
              <p className="font-medium text-foreground">{COPY.createdTitle}</p>
              <p className="text-sm">{COPY.createdHint}</p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <code className="break-all rounded bg-muted px-2 py-1 text-sm">{created.link}</code>
                <Button size="sm" variant="outline" onClick={() => void copyLink(created.link)}>
                  <Copy className="mr-2 h-4 w-4" />
                  {COPY.copyLink}
                </Button>
              </div>
              {created.emailed && created.email && <p className="text-sm">{COPY.emailed(created.email)}</p>}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">{COPY.listTitle}</p>
          {rows === null && !error && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {rows !== null && rows.length === 0 && <p className="text-sm text-muted-foreground">{COPY.listEmpty}</p>}
          {rows !== null && rows.length > 0 && (
            <ul className="divide-y divide-border rounded-md border border-border">
              {rows.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 p-3" data-testid="knock-invite-row">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{r.label}</p>
                    {r.detail && <p className="truncate text-xs text-muted-foreground">{r.detail}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant={r.status === 'pending' ? 'default' : 'secondary'}>{COPY.statusLabel[r.status]}</Badge>
                    {r.status === 'pending' && (
                      <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => void revoke(r)}>
                        {busy === r.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {COPY.revoke}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}
